<template>
  <div class="cc-report-dashboard-container">
    <div class="cc-main-content">
    <div class="cc-header">
      <span class="cc-header-title">{{ t('consultantCorner.ReportDashboard') }}</span>
    </div>

    <!-- Notification Bar -->
    <div class="cc-metric-card title-bar">
      <div class="cc-notification-content">
        {{ t('consultantCorner.CongratulationsMessageStart') }}
        <span class="cc-title-highlight">{{ consultantTitle }}</span>
        {{ t('consultantCorner.CongratulationsMessageEnd') }}
      </div>
    </div>



    <!-- Metrics Cards -->
    <div class="cc-metrics-grid">
      <a :href="buildReportHref('personalTeamSales')" class="cc-metric-card">
        <div class="cc-metric-icon">🏆</div>
        <div class="cc-metric-content">
          <div class="cc-metric-label">{{ t('consultantCorner.PersonalTeamSales') }}</div>
          <div class="cc-metric-value">{{ FormatCurrency(personalTeamSales, null) }}</div>
        </div>
      </a>

      <a :href="buildReportHref('organizationSales')" class="cc-metric-card">
        <div class="cc-metric-icon">💰</div>
        <div class="cc-metric-content">
          <div class="cc-metric-label">{{ t('consultantCorner.OrganizationSales') }}</div>
          <div class="cc-metric-value">{{ FormatCurrency(organizationSales, null) }}</div>
        </div>
      </a>

      <a :href="buildReportHref('TeamMembersDownlineSummary')" class="cc-metric-card">
        <div class="cc-metric-icon">➕</div>
        <div class="cc-metric-content">
          <div class="cc-metric-label">{{ t('consultantCorner.TeamRecruits') }}</div>
          <div class="cc-metric-value">{{ indicators.teamMembersDownlineSummary }}</div>
        </div>
      </a>

      <a :href="buildReportHref('TeamMembersCount')" class="cc-metric-card">
        <div class="cc-metric-icon">👥</div>
        <div class="cc-metric-content">
          <div class="cc-metric-label">{{ t('consultantCorner.TeamEndCount') }}</div>
          <div class="cc-metric-value">{{ indicators.teamMembersCount }}</div>
        </div>
      </a>
    </div>

    <!-- Performance Indicators -->
    <div class="cc-performance-sections">
      <div class="cc-performance-section">
        <h3 class="cc-section-title">{{ t('consultantCorner.GettingStarted') }}</h3>
        <div class="cc-indicator-grid">
          <a class="cc-indicator-item cc-start" :href="getIndicatorHref('new-consultants')">
            <div class="cc-indicator-number">{{ indicators.newConsultants }}</div>
            <div class="cc-indicator-label">{{ t('consultantCorner.NewConsultants') }}</div>
          </a>
          <a class="cc-indicator-item cc-start" :href="getIndicatorHref('senior-consultants')">
            <div class="cc-indicator-number">{{ indicators.seniorConsultants }}</div>
            <div class="cc-indicator-label">{{ t('consultantCorner.SeniorConsultantsAndTeamLeaders') }}</div>
          </a>
        </div>
      </div>

      <div class="cc-performance-section">
        <h3 class="cc-section-title">{{ t('consultantCorner.AtRisk') }}</h3>
        <div class="cc-indicator-grid">
          <a class="cc-indicator-item cc-risk-item" :href="getIndicatorHref('not-paid-title')">
            <div class="cc-indicator-number">{{ indicators.notPaidAtTitle }}</div>
            <div class="cc-indicator-label">{{ t('consultantCorner.NotPaidAtTitle') }}</div>
          </a>
          <a class="cc-indicator-item cc-risk-item" :href="getIndicatorHref('inactive')">
            <div class="cc-indicator-number">{{ indicators.inactive }}</div>
            <div class="cc-indicator-label">{{ t('consultantCorner.Inactive') }}</div>
          </a>
          <a class="cc-indicator-item cc-risk-item" :href="getIndicatorHref('losing-career-sales')">
            <div class="cc-indicator-number">{{ indicators.losingCareerSales }}</div>
            <div class="cc-indicator-label">{{ t('consultantCorner.CloseToLosingCareerSales') }}</div>
          </a>
          <a class="cc-indicator-item cc-risk-item" :href="getIndicatorHref('few-parties')">
            <div class="cc-indicator-number">{{ indicators.fewPartiesBooked }}</div>
            <div class="cc-indicator-label">{{ t('consultantCorner.LessThan3PartiesBooked') }}</div>
          </a>
        </div>
      </div>

      <div class="cc-performance-section">
        <h3 class="cc-section-title">{{ t('consultantCorner.RoadToSuccess') }}</h3>
        <div class="cc-indicator-grid">
          <a class="cc-indicator-item cc-success-item" :href="getIndicatorHref('close-promoting')">
            <div class="cc-indicator-number">{{ indicators.closeToPromoting }}</div>
            <div class="cc-indicator-label">{{ t('consultantCorner.CloseToPromoting') }}</div>
          </a>
          <a class="cc-indicator-item cc-success-item" :href="getIndicatorHref('travel-incentive')">
            <div class="cc-indicator-number">{{ indicators.closeToTravelIncentive }}</div>
            <div class="cc-indicator-label">{{ t('consultantCorner.CloseToEarningTravelIncentive') }}</div>
          </a>
          <a class="cc-indicator-item cc-success-item" :href="getIndicatorHref('sales-promo')">
            <div class="cc-indicator-number">{{ indicators.closeToSalesPromo }}</div>
            <div class="cc-indicator-label">{{ t('consultantCorner.CloseToEarningSalesPromo') }}</div>
          </a>
        </div>
      </div>
    </div>
        <!-- Time Period Filter -->

    <div class="cc-team-section">
      <div class="cc-section-header">
        <h3 class="cc-section-title">{{ t('consultantCorner.MyPersonalTeam') }}</h3>
        <div class="cc-team-filters">
          <DropDownList
            :data-items="levelFilterOptions"
            :value="selectedLevelFilter"
            @change="onLevelFilterChange"
            :textField="'text'"
            :valueField="'value'"
            :valuePrimitive="true"
          />
          <DropDownList
            :data-items="statusFilterOptions"
            :value="selectedStatusFilter"
            @change="onStatusFilterChange"
            :textField="'text'"
            :valueField="'value'"
            :valuePrimitive="true"
          />
        </div>
      </div>

      <!-- Desktop Grid View -->
      <div class="cc-team-desktop-view">
      <div class="cc-team-grid-wrapper">
        <div class="cc-team-grid">
          <div class="cc-grid-header">
            <div class="cc-grid-header-cell" @click="sortBy('status')">
              {{ t('consultantCorner.Status') }}
              <span v-if="sortField === 'status'" :class="sortDirection === 'asc' ? 'cc-sort-asc' : 'cc-sort-desc'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
            </div>
            <div class="cc-grid-header-cell" @click="sortBy('name')">
              {{ t('consultantCorner.Name') }}
              <span v-if="sortField === 'name'" :class="sortDirection === 'asc' ? 'cc-sort-asc' : 'cc-sort-desc'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
            </div>
            <div class="cc-grid-header-cell" @click="sortBy('title')">
              {{ t('consultantCorner.Title') }}
              <span v-if="sortField === 'title'" :class="sortDirection === 'asc' ? 'cc-sort-asc' : 'cc-sort-desc'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
            </div>
            <div class="cc-grid-header-cell" @click="sortBy('personalSales')">
              {{ t('consultantCorner.PersonalSales') }}
              <span v-if="sortField === 'personalSales'" :class="sortDirection === 'asc' ? 'cc-sort-asc' : 'cc-sort-desc'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
            </div>
            <div class="cc-grid-header-cell" @click="sortBy('personalTeamSales')">
              {{ t('consultantCorner.PersonalTeamSales') }}
              <span v-if="sortField === 'personalTeamSales'" :class="sortDirection === 'asc' ? 'cc-sort-asc' : 'cc-sort-desc'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
            </div>
            <div class="cc-grid-header-cell" @click="sortBy('teamRecruits')">
              {{ t('consultantCorner.TeamRecruits') }}
              <span v-if="sortField === 'teamRecruits'" :class="sortDirection === 'asc' ? 'cc-sort-asc' : 'cc-sort-desc'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
            </div>
            <div class="cc-grid-header-cell" @click="sortBy('teamEndCount')">
              {{ t('consultantCorner.TeamEndCount') }}
              <span v-if="sortField === 'teamEndCount'" :class="sortDirection === 'asc' ? 'cc-sort-asc' : 'cc-sort-desc'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
            </div>
            <div class="cc-grid-header-cell" @click="sortBy('activeConsultants')">
              {{ t('consultantCorner.ActiveConsultants') }}
              <span v-if="sortField === 'activeConsultants'" :class="sortDirection === 'asc' ? 'cc-sort-asc' : 'cc-sort-desc'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
            </div>

          </div>

          <div class="cc-grid-body">
            <div v-for="member in paginatedTeamMembers" :key="member.beeNumber" class="cc-grid-row">
              <div class="cc-grid-cell">
                <div class="cc-status-indicator" :class="getStatusClass(member.status)"></div>
              </div>
              <div class="cc-grid-cell cc-name-cell cc-clickable-name" @click="viewTeamMemberDetails(member, $event)">{{ member.name }}</div>
              <div class="cc-grid-cell">{{ member.title }}</div>
              <div class="cc-grid-cell">{{ FormatCurrency(member.personalSales, null) }}</div>
              <div class="cc-grid-cell">{{ FormatCurrency(member.personalTeamSales, null) }}</div>
              <div class="cc-grid-cell">{{ member.teamRecruits }}</div>
              <div class="cc-grid-cell">{{ member.teamEndCount }}</div>
              <div class="cc-grid-cell">{{ member.activeConsultants }}</div>
            </div>
          </div>
        </div>
      </div>
      </div>

      <!-- Mobile Card View -->
      <div class="cc-team-mobile-card-view">
        <div v-for="member in paginatedTeamMembers" :key="member.beeNumber" class="cc-member-card">
          <div class="cc-member-card-header" @click="viewTeamMemberDetails(member, $event)">
            <div class="cc-member-card-name-row">
              <div class="cc-status-indicator" :class="getStatusClass(member.status)"></div>
              <span class="cc-member-card-name">{{ member.name }}</span>
            </div>
            <span class="cc-member-card-title">{{ member.title }}</span>
          </div>
          <div class="cc-member-card-body">
            <div class="cc-member-card-row">
              <span class="cc-member-card-label">{{ t('consultantCorner.PersonalSales') }}</span>
              <span class="cc-member-card-value">{{ FormatCurrency(member.personalSales, null) }}</span>
            </div>
            <div class="cc-member-card-row">
              <span class="cc-member-card-label">{{ t('consultantCorner.PersonalTeamSales') }}</span>
              <span class="cc-member-card-value">{{ FormatCurrency(member.personalTeamSales, null) }}</span>
            </div>
            <div class="cc-member-card-row">
              <span class="cc-member-card-label">{{ t('consultantCorner.TeamRecruits') }}</span>
              <span class="cc-member-card-value">{{ member.teamRecruits }}</span>
            </div>
            <div class="cc-member-card-row">
              <span class="cc-member-card-label">{{ t('consultantCorner.TeamEndCount') }}</span>
              <span class="cc-member-card-value">{{ member.teamEndCount }}</span>
            </div>
            <div class="cc-member-card-row">
              <span class="cc-member-card-label">{{ t('consultantCorner.ActiveConsultants') }}</span>
              <span class="cc-member-card-value">{{ member.activeConsultants }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Consultant Detail Card (inline) -->
      <div v-if="showConsultantDetail" class="cc-inline-detail-card">
        <CCConsultantDetailCard
          :is-open="showConsultantDetail"
          :bee-number="selectedConsultantBeeNumber"
          :consultant-name="selectedConsultantName"
          :status="selectedConsultantStatus"
          :phone="selectedConsultantPhone"
          :email="selectedConsultantEmail"
          :card-top="0"
          :hide-extra-fields="true"
          @close="closeConsultantDetail"
        />
      </div>

      <div class="cc-pagination-container">
        <Pager
          :skip="(currentPage - 1) * pageSize"
          :take="pageSize"
          :total="totalTeamMembers"
          :button-count="5"
          :responsive="true"
          :info="true"
          :type="'numeric'"
          :page-sizes="[10, 25, 50, 100]"
          :previous-next="true"
          @pagechange="onPageChange"
        />
      </div>
    </div>
    </div>

    <!-- Leaderboard and Recognition -->
     <div class="cc-leaderboard-container">

    <div class="cc-leaderboard-section">
      <div class="cc-time-filter ">
         <div class="cc-filter-buttons">
           <button
              v-for="option in timePeriodOptions"
                  :key="option.value"
                  :class="['cc-filter-button', { 'active': selectedTimePeriod === option.value }]"
                  @click="onTimePeriodChange(option.value)"
                >
                  {{ option.text }}
              </button>
         </div>
       </div>
      <div class="cc-section-header">
        <h3 class="cc-section-title">{{ t('consultantCorner.Leaderboard') }}</h3>

      </div>
      <div class="cc-leaderboard-filters">
          <DropDownList
            :data-items="leaderboardFilterOptions"
            :value="selectedLeaderboardFilter"
            @change="onLeaderboardFilterChange"
            :textField="'text'"
            :valueField="'value'"
            :valuePrimitive="true"
          />
          <DropDownList
            :data-items="rowCountOptions"
            :value="selectedRowCount"
            @change="onRowCountChange"
            :textField="'text'"
            :valueField="'value'"
            :valuePrimitive="true"
          />
        </div>
      <div class="cc-leaderboard-grid">
        <div v-for="(leader, index) in filteredLeaderboard.slice(0, selectedRowCount)" :key="leader.beeNumber" class="cc-leader-item">
          <div class="cc-leader-rank">{{ index + 1 }}</div>
          <div class="cc-leader-name">{{ leader.name }}</div>
          <div class="cc-leader-value">{{ formatLeaderValue(leader.value) }}</div>
        </div>
      </div>
                <KButton @click="generateRecognitionImage" class="cc-recognition-button">
            {{ t('consultantCorner.GenerateRecognitionImage') }}
          </KButton>
    </div>
    </div>
    </div>

    <!-- Team Grid -->
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { DropDownList } from '@progress/kendo-vue-dropdowns'
import { Pager } from '@progress/kendo-vue-data-tools'
import { PwsPersonalMetricsGet, PwsTeamStructureGet, PwsTeamDetailsGet, PwsAppPeriodDateGetList, PwsBusinessDetailsGet, PwsPerformanceMetricsSummaryGet, PwsPerformanceMetricsGet } from '@/api/ConsultantCorner'
import { GetBeeNumberAuthenicatedUser, GetReportUrl } from '../../stores/SettingsStore'
import { handleError } from '../../services/ErrorService'
import { FormatCurrency } from '../../services/currencyFormatter'
import CCConsultantDetailCard from './CCConsultantDetailCard.vue'
import type { IBusinessEntityPerformanceMetricGroup, ITeamMember } from '../../types/ConsultantCornerTypes'
import type { ITeamStructureResponse, ITeamMemberResponse, ITeamDetailsResponse, ITeamDetailsField, ITeamDetailsTeamMember } from '../../types/TeamStructureTypes'
import { CUSTOMER_CLASS_ID } from '@/constants/Classes'
import { REPORT_EXCLUDED_STATUS_IDS } from '@/constants/ConsultantStatus'

const { t } = useI18n()
const logger = useLogger()

const loading = ref(false)
const consultantTitle = ref(t('consultantCorner.reportDashboard.defaultConsultantTitle'))
const selectedTimePeriod = ref('thisMonth')
const selectedLeaderboardFilter = ref('newrecs')
const selectedRowCount = ref(10)
const monthDetailsResponse = ref<any>(null)
const yearDetailsResponse = ref<any>(null)
const detailsResponse = computed(() =>
  selectedTimePeriod.value === 'thisYear' ? yearDetailsResponse.value : monthDetailsResponse.value
)

// Store structure response for status info
const structureResponse = ref<ITeamStructureResponse | null>(null)

// Track if we've already loaded data for each period
const monthDataLoaded = ref(false)
const yearDataLoaded = ref(false)
const selectedLevelFilter = ref('allLevels')
const selectedStatusFilter = ref('allStatus')
const sortField = ref('name')
const sortDirection = ref('asc')

// Pagination state
const currentPage = ref(1)
const pageSize = ref(10)

// Consultant detail card state
const showConsultantDetail = ref(false)
const selectedConsultantBeeNumber = ref<string | null>(null)
const selectedConsultantName = ref('')
const selectedConsultantStatus = ref('')
const selectedConsultantPhone = ref('')
const selectedConsultantEmail = ref('')

const organizationSales = ref(0)
const personalTeamSales = ref(0)
const teamRecruits = ref(0)
const teamEndCount = ref(0)

// Store the raw performance metrics response
const performanceMetrics = ref<any>(null)

// Map indicator types to systemKeywords
const indicatorSystemKeywords = {
  'new-consultants': 'NewConsultants',
  'senior-consultants': 'TeamMembersLeaders',
  'not-paid-title': 'TeamMembersNotBeingPaidAtTitle',
  'inactive': 'TeamMembersInactive',
  'losing-career-sales': 'TeamMembersCloseToBeDropped',
  'few-parties': 'TeamMembersWithFewBookings',
  'close-promoting': 'TeamMembersCloseToPromotion',
  'travel-incentive': 'TeamMembersCloseToTripIncentive',
  'sales-promo': 'TeamMembersCloseToSalesPromo'
}

// Helper function to find metric value by systemKeyword
function findMetricValue(systemKeyword: string): number {
  if (!performanceMetrics.value?.items) return 0

  for (const group of performanceMetrics.value.items) {
    const metric = group.metrics?.find((m: { systemKeyword: string; metricValue: number }) =>
      m.systemKeyword === systemKeyword
    )
    if (metric?.metricValue !== undefined) return metric.metricValue
  }
  return 0
}

// Computed property to map performance metrics to indicators
const indicators = computed(() => {
  const result = {
    newConsultants: findMetricValue('NewConsultants'),
    seniorConsultants: findMetricValue('TeamMembersLeaders'), // Assuming TeamLeaders maps to seniorConsultants
    notPaidAtTitle: findMetricValue('TeamMembersNotBeingPaidAtTitle'),
    inactive: findMetricValue('TeamMembersInactive'),
    losingCareerSales: findMetricValue('TeamMembersCloseToBeDropped'),
    fewPartiesBooked: findMetricValue('TeamMembersWithFewBookings'),
    closeToPromoting: findMetricValue('TeamMembersCloseToPromotion'),
    closeToTravelIncentive: findMetricValue('TeamMembersCloseToTripIncentive'),
    closeToSalesPromo: findMetricValue('TeamMembersCloseToSalesPromo'),
    teamMembersDownlineSummary: findMetricValue('TeamMembersDownlineSummary'),
    teamMembersCount: findMetricValue('TeamMembersCount')
  }
  return result
})

// Initialize empty team members array that will be populated by loadTeamData
const teamMembers = ref<ITeamMember[]>([])

// Initialize empty leaderboard array that will be populated by updateLeaderboard
const leaderboard = ref<Array<{ beeNumber: number; name: string; value: number }>>([])

const timePeriodOptions = computed(() => [
  { text: t('consultantCorner.ThisMonth'), value: 'thisMonth' },
  { text: t('consultantCorner.ThisYear'), value: 'thisYear' }
])

const leaderboardFilterOptions = computed(() => [
  { text: t('consultantCorner.Recruits'), value: 'newrecs' },
  { text: t('consultantCorner.QualifiedRecruits'), value: 'NumNewQualRecs' },
  { text: t('consultantCorner.CommissionableSales'), value: 'TotalPersCommVol' }
])

const rowCountOptions = computed(() => [
  { value: 10, labelKey: 'labels.options.Show10', text: t('labels.options.Show10') },
  { value: 15, labelKey: 'labels.options.Show15', text: t('labels.options.Show15') },
  { value: 20, labelKey: 'labels.options.Show20', text: t('labels.options.Show20') }
])

const levelFilterOptions = computed(() => {
  // Always include 'All Levels' option
  const options = [{ text: t('consultantCorner.AllLevels'), value: 'allLevels' }]

  if (structureResponse.value?.item?.teamMembers) {
    // Get unique levels from team members
    const uniqueLevels = [...new Set(structureResponse.value.item.teamMembers.map(
      member => member.compensationLevel.levelName
    ))].sort()

    // Add each unique level to options
    uniqueLevels.forEach(level => {
      options.push({ text: level, value: level })
    })
  }

  return options
})

const statusFilterOptions = computed(() => {
  // Always include 'All Status' option
  const options = [{ text: t('consultantCorner.AllStatus'), value: 'allStatus' }]

  if (structureResponse.value?.item?.teamMembers) {
    // Get unique statuses from team members
    const uniqueStatuses = [...new Set(structureResponse.value.item.teamMembers.map(
      member => member.status.statusName
    ))].sort()

    // Add each unique status to options
    uniqueStatuses.forEach(statusName => {
      options.push({
        text: statusName,
        value: statusName
      })
    })
  }

  return options
})

const filteredTeamMembers = computed(() => {
  let filtered = teamMembers.value

  // Filter by level if not 'allLevels'
  if (selectedLevelFilter.value !== 'allLevels') {
    filtered = filtered.filter(member => member.title === selectedLevelFilter.value)
  }

  // Filter by status if not 'allStatus'
  if (selectedStatusFilter.value !== 'allStatus') {
    filtered = filtered.filter(member => {
      const memberStatus = structureResponse.value?.item?.teamMembers.find(
        structureMember => structureMember.beeNumber.beeNumber === member.beeNumber
      )?.status.statusName || ''
      return memberStatus === selectedStatusFilter.value
    })
  }

  // Sort the filtered results
  const sorted = [...filtered].sort((a, b) => {
    const aValue = a[sortField.value as keyof ITeamMember]
    const bValue = b[sortField.value as keyof ITeamMember]

    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0
    if (aValue == null) return 1
    if (bValue == null) return -1

    // Compare values based on type
    let comparison = 0
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      comparison = aValue.localeCompare(bValue)
    } else if (typeof aValue === 'number' && typeof bValue === 'number') {
      comparison = aValue - bValue
    } else {
      comparison = String(aValue).localeCompare(String(bValue))
    }

    return sortDirection.value === 'asc' ? comparison : -comparison
  })

  return sorted
})

const paginatedTeamMembers = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  const end = start + pageSize.value
  const sliceToDisplay = filteredTeamMembers.value.slice(start, end)
  return sliceToDisplay
})

const totalTeamMembers = computed(() => filteredTeamMembers.value.length)

const filteredLeaderboard = computed(() => {
  return leaderboard.value.slice(0, selectedRowCount.value).sort((a, b) => b.value - a.value)
})

const formatLeaderValue = (value: number): string => {
  if (selectedLeaderboardFilter.value === 'TotalPersCommVol') {
    return FormatCurrency(value, null) || ''
  }
  // Ensure max 2 decimal places for numeric values
  return Number.isInteger(value) ? value.toString() : value.toFixed(2)
}

const getStatusClass = (status: string): string => {
  switch (status) {
    case 'green': return 'cc-status-green'
    case 'yellow': return 'cc-status-yellow'
    case 'red': return 'cc-status-red'
    default: return 'cc-status-gray'
  }
}

const onTimePeriodChange = async (value: string) => {
  selectedTimePeriod.value = value
  logger.info('Time period changed to:', value)

  const periodDatesResponse = await PwsAppPeriodDateGetList()
  if (!periodDatesResponse?.items?.length) {
    throw new Error(t('consultantCorner.reportDashboard.errors.noPeriodDatesAvailable'))
  }
  const batchNumber = periodDatesResponse.items[0].batchNumber

  // Load data if not already loaded for the selected period
  if (value === 'thisYear' && !yearDataLoaded.value) {
    await loadTeamData(batchNumber)
    yearDataLoaded.value = true
  } else if (value === 'thisMonth' && !monthDataLoaded.value) {
    await loadTeamData(batchNumber)
    monthDataLoaded.value = true
  } else {
    // Just update the grid and leaderboard with cached data
    updateTeamGrid()
    updateLeaderboard()
  }
}

const onLeaderboardFilterChange = (event: any) => {
  selectedLeaderboardFilter.value = event.value
  updateLeaderboard()
}

const onRowCountChange = (event: any) => {
  selectedRowCount.value = event.value
  updateLeaderboard()
}

const onLevelFilterChange = (event: any) => {
  selectedLevelFilter.value = event.value
  currentPage.value = 1 // Reset to first page when filter changes
  logger.info('Level filter changed to:', event.value)
}

const onStatusFilterChange = (event: any) => {
  selectedStatusFilter.value = event.value
  currentPage.value = 1 // Reset to first page when filter changes
  logger.info('Status filter changed to:', event.value)
}

const onPageChange = (event: any) => {
  const { skip, take } = event;
  currentPage.value = (skip / take) + 1
  pageSize.value = event.take
}

const sortBy = (field: string) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortDirection.value = 'asc'
  }
  // Reset to page 1 when sorting changes
  currentPage.value = 1
}

const reportBaseUrl = ref<string>('')

const buildReportHref = (reportId: string): string => {
  if (!reportBaseUrl.value || !reportId) return '#'
  return `${reportBaseUrl.value}?id=${encodeURIComponent(reportId)}`
}

const getIndicatorHref = (type: string): string => {
  const systemKeyword = indicatorSystemKeywords[type as keyof typeof indicatorSystemKeywords]
  if (systemKeyword) {
    // Find the metric to get its redirect parameters
    for (const group of performanceMetrics.value?.items || []) {
      const metric = group.metrics?.find((m: { systemKeyword: string; redirect?: { parameters?: { ReportId?: string } } }) =>
        m.systemKeyword === systemKeyword
      )
      if (metric?.redirect?.parameters?.ReportId) {
        return buildReportHref(metric.redirect.parameters.ReportId)
      }
    }
    // Fallback if no redirect found in metric
    return buildReportHref(systemKeyword)
  }
  return '#'
}

const viewTeamMemberDetails = (member: ITeamMember, event: MouseEvent) => {
  logger.info('View team member details:', member)
  logger.info('Status name:', member.statusName)
  logger.info('Email:', member.email)

  // Set consultant details
  selectedConsultantBeeNumber.value = member.beeNumber.toString()
  selectedConsultantName.value = member.name
  selectedConsultantStatus.value = member.statusName || ''
  selectedConsultantPhone.value = '' // Phone not available in current data
  selectedConsultantEmail.value = member.email || ''
  showConsultantDetail.value = true
}

const closeConsultantDetail = () => {
  showConsultantDetail.value = false
}

const generateRecognitionImage = () => {
  logger.info('Generate recognition image')

  // Check if we have a leaderboard winner
  if (!leaderboard.value || leaderboard.value.length === 0) {
    logger.warn('No leaderboard data available')
    return
  }

  const winner = leaderboard.value[0]
  const metricName = leaderboardFilterOptions.value.find(opt => opt.value === selectedLeaderboardFilter.value)?.text || 'Performance'

  // Create canvas
  const canvas = document.createElement('canvas')
  canvas.width = 800
  canvas.height = 600
  const ctx = canvas.getContext('2d')

  if (!ctx) return

  // Background gradient
  const gradient = ctx.createLinearGradient(0, 0, 0, 600)
  gradient.addColorStop(0, '#2c7a87')
  gradient.addColorStop(1, '#1e4b54')
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, 800, 600)

  // Add decorative elements
  ctx.fillStyle = 'rgba(255, 255, 255, 0.1)'
  ctx.beginPath()
  ctx.arc(700, 100, 150, 0, Math.PI * 2)
  ctx.fill()
  ctx.beginPath()
  ctx.arc(100, 500, 100, 0, Math.PI * 2)
  ctx.fill()

  // Title
  ctx.fillStyle = '#ffffff'
  ctx.font = 'bold 48px Arial'
  ctx.textAlign = 'center'
  ctx.fillText(`🏆 ${t('consultantCorner.RecognitionCongratulations')} 🏆`, 400, 100)

  // Winner name
  ctx.font = 'bold 56px Arial'
  ctx.fillText(winner.name, 400, 220)

  // Achievement text
  ctx.font = '32px Arial'
  ctx.fillText(t('consultantCorner.RecognitionFirstPlace'), 400, 300)

  // Metric details
  ctx.font = 'bold 40px Arial'
  ctx.fillStyle = '#ffd700'
  const valueText = selectedLeaderboardFilter.value === 'newrecs'
    ? `${winner.value} ${metricName}`
    : FormatCurrency(winner.value, null) || `${winner.value}`
  ctx.fillText(valueText, 400, 380)

  // Additional message
  ctx.fillStyle = '#ffffff'
  ctx.font = '28px Arial'
  ctx.fillText(t('consultantCorner.RecognitionOutstanding', { metric: metricName }), 400, 450)

  // Footer
  ctx.font = 'italic 20px Arial'
  ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
  const timePeriod = timePeriodOptions.value.find(opt => opt.value === selectedTimePeriod.value)?.text || 'This Period'
  ctx.fillText(`${timePeriod} - ${new Date().toLocaleDateString()}`, 400, 550)

  // Convert to blob and download
  canvas.toBlob((blob) => {
    if (!blob) return

    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.download = `recognition-${winner.name.replace(/\s+/g, '-')}-${Date.now()}.png`
    link.href = url
    link.click()
    URL.revokeObjectURL(url)

    logger.info('Recognition image generated for:', winner.name)
  })
}

const loadMetrics = async (batchNumber: number) => {
  try {
    loading.value = true

    // Request all required metrics
    logger.info('Requesting metrics')

    const [metricsResponse, periodDatesResponse] = await Promise.all([
      PwsBusinessDetailsGet({
        batchNumber,
        fieldName: ['OrgVolume', 'TotalPersCommVol', 'NewRecs', 'PersCons','PaidAtLevelText','PaidAtLevel','TotalTeamCommVolume']
      }),
      PwsAppPeriodDateGetList()
    ])



    // Assign metrics to reactive variables
    if (metricsResponse?.currentFields) {
      // Find each metric by fieldName and assign its value
      metricsResponse.currentFields.forEach((field: any) => {
        switch (field.fieldName) {
          case 'OrgVolume':
            organizationSales.value = field.value || 0
            break
          case 'TotalTeamCommVolume':
            personalTeamSales.value = field.value || 0
            break
          case 'NewRecs':
            teamRecruits.value = field.value || 0
            break
          case 'PersCons':
            teamEndCount.value = field.value || 0
            break
        }
      })

      logger.info('Metrics loaded:', {
        organizationSales: organizationSales.value,
        personalTeamSales: personalTeamSales.value,
        teamRecruits: teamRecruits.value,
        teamEndCount: teamEndCount.value,
        fullResponse: metricsResponse
      })
    }

    // Log the period dates
    if (periodDatesResponse) {
      logger.info('Period dates:', {
        response: periodDatesResponse
      })
    }
  } catch (error: any) {
    // Add detailed error logging
    logger.error('Error loading data:', {
      message: error.message,
      errorCode: error.responseStatus?.errorCode,
      errorMessage: error.responseStatus?.message,
      stack: error.stack
    })
    await handleError(error, t)
  } finally {
    loading.value = false
  }
}

const getStatusColor = (
  statusId: string,
  personalSales: number,
  teamRecruits: number,
  paidAtLevelText: string | null,
  compensationLevelName: string | null
): 'green' | 'yellow' | 'red' | 'gray' => {
  if (personalSales === 0 && teamRecruits === 0 && statusId === 'I') {
    return 'red'
  }
  if (paidAtLevelText !== null && compensationLevelName !== null) {
    // Both are human-readable title text (e.g. "Consultant"); PaidAtLevel is a GUID and
    // compensationLevelId is a short code, so those two are not comparable to each other.
    return String(paidAtLevelText) === String(compensationLevelName) ? 'green' : 'yellow'
  }
  return 'gray'
}

const updateLeaderboard = () => {
  if (!detailsResponse.value?.item?.team) return

  const leaderboardData: { beeNumber: number; name: string; value: number }[] = []

  detailsResponse.value.item.team.forEach((item: any) => {
    const member = item.teamMember
    const fieldList = item.fieldList || []

    if (fieldList.length > 0) {
      const metricField = fieldList.find((f: ITeamDetailsField) => f.fieldName === selectedLeaderboardFilter.value)
      if (metricField?.value) {
        leaderboardData.push({
          beeNumber: member.beeNumber.beeNumber,
          name: `${member.firstName} ${member.lastName}`,
          value: metricField.value
        })
      }
    }
  })

  // Sort by value and take top 3
  const topPerformers = leaderboardData
    .sort((a, b) => b.value - a.value)
    .slice(0, selectedRowCount.value)
  leaderboard.value = topPerformers

  logger.info('Leaderboard updated for:', selectedLeaderboardFilter.value)
  logger.debug(selectedRowCount.value, 'top performers:', topPerformers)
}


const loadTeamDetailsForPeriod = async (batchNumber: number) => {
  return PwsTeamDetailsGet({
    batchNumber,
    maxLevelsDisplayed: 5,
    reportName: 'TEAMDETAILS_MyTeamReportDashboard',
    fieldList: ['TotalPersCommVol', 'TotalTeamCommVolume', 'newrecs', 'PersCons', 'PaidAtLevel', 'PaidAtLevelText', 'ActiveCons', 'NumNewQualRecs']
  })
}

const aggregateTeamDetails = (allDetails: any[]) => {
  const aggregatedTeam = new Map<number, any>()

  allDetails.forEach(details => {
    if (!details?.item?.team) return

    details.item.team.forEach((item: any) => {
      const member = item.teamMember
      const beeNumber = member.beeNumber.beeNumber
      const fieldList = item.fieldList || []

      if (!aggregatedTeam.has(beeNumber)) {
        // First time seeing this member, initialize their data
        aggregatedTeam.set(beeNumber, {
          teamMember: member,
          fieldList: fieldList.map((f: ITeamDetailsField) => ({ ...f, value: 0 }))
        })
      }

      // Add this period's values to the running totals
      const memberData = aggregatedTeam.get(beeNumber)
      fieldList.forEach((field: ITeamDetailsField) => {
        const existingField = memberData.fieldList.find((f: ITeamDetailsField) => f.fieldName === field.fieldName)
        if (existingField) {
          existingField.value += field.value || 0
        }
      })
    })
  })

  return {
    item: {
      team: Array.from(aggregatedTeam.values())
    }
  }
}

const updateTeamGrid = () => {
  if (!detailsResponse.value?.item?.team || !structureResponse.value?.item?.teamMembers) return

  // Create a map of team members from structure response to get status and title
  const structureMap = new Map(structureResponse.value.item.teamMembers.map((member) => [
    member.beeNumber.beeNumber,
    {
      status: member.status,
      title: member.compensationLevel.levelName
    }
  ]))

  // Update the team grid with current period's data
  teamMembers.value = detailsResponse.value.item.team.map((item: any) => {
    const member = item.teamMember
    const fieldList = item.fieldList || []
    const structureMember = structureMap.get(member.beeNumber.beeNumber)

    const memberPersonalSales = fieldList.find((f: ITeamDetailsField) => f.fieldName === 'TotalPersCommVol')?.value || 0
    const memberTeamRecruits = fieldList.find((f: ITeamDetailsField) => f.fieldName === 'newrecs')?.value || 0
    const paidAtLevelTextField = fieldList.find((f: ITeamDetailsField) => f.fieldName === 'PaidAtLevelText')
    const memberPaidAtLevelText = paidAtLevelTextField ? String(paidAtLevelTextField.value) : null
    const memberCompLevelName = member.compensationLevel?.levelName
      ? String(member.compensationLevel.levelName)
      : null
    const teamMember = {
      beeNumber: member.beeNumber.beeNumber,
      name: `${member.firstName} ${member.lastName}`,
      title: member.compensationLevel?.levelName || structureMember?.title || 'Executive Director',
      status: getStatusColor(
        member.status?.statusId || structureMember?.status?.statusId || 'I',
        memberPersonalSales,
        memberTeamRecruits,
        memberPaidAtLevelText,
        memberCompLevelName
      ),
      statusName: member.status?.statusName || '',
      email: member.emailAddress || '',
      personalSales: memberPersonalSales,
      personalTeamSales: fieldList.find((f: ITeamDetailsField) => f.fieldName === 'TotalTeamCommVolume')?.value || 0,
      teamRecruits: memberTeamRecruits,
      teamEndCount: fieldList.find((f: ITeamDetailsField) => f.fieldName === 'PersCons')?.value || 0,
      activeConsultants: fieldList.find((f: ITeamDetailsField) => f.fieldName === 'ActiveCons')?.value || 0
    }

    logger.info('Created team member:', teamMember)
    return teamMember
  })

  logger.info('Total team members created:', teamMembers.value.length)
}

const loadTeamData = async (batchNumber: number) => {
  try {
    const [structure, periodDatesResponse, businessDetailsResponse] = await Promise.all([
      PwsTeamStructureGet({
        batchNumber,
        maxLevelsDisplayed: 5
      }),
      PwsAppPeriodDateGetList(),
      PwsBusinessDetailsGet({
        batchNumber,
        fieldName: ['PaidAtLevelText']
      })
    ])

    let details
    if (selectedTimePeriod.value === 'thisYear' && periodDatesResponse?.items) {
      // Get current period's year
      const currentPeriod = periodDatesResponse.items.find((p: { batchNumber: number, startDate: string }) => p.batchNumber === batchNumber)
      if (!currentPeriod) throw new Error(t('consultantCorner.reportDashboard.errors.currentPeriodNotFound'))

      const currentYear = new Date(currentPeriod.startDate).getFullYear()

      // Filter periods that are in the same year and up to current period
      const periodsToProcess = periodDatesResponse.items
        .filter((period: { startDate: string, batchNumber: number }) => {
          const periodYear = new Date(period.startDate).getFullYear()
          return periodYear === currentYear && period.batchNumber <= currentPeriod.batchNumber
        })

      logger.info('Periods to process:', periodsToProcess)

      // Use Promise.allSettled to continue even if some periods fail
      const periodDetailsResults = await Promise.allSettled(
        periodsToProcess.map((period: { batchNumber: number }) => loadTeamDetailsForPeriod(period.batchNumber))
      )

      // Filter out failed requests and extract successful values
      const periodDetails = periodDetailsResults
        .filter((result): result is PromiseFulfilledResult<any> => {
          if (result.status === 'rejected') {
            logger.warn('Failed to load period data:', result.reason)
            return false
          }
          return true
        })
        .map(result => result.value)

      logger.info(`Successfully loaded ${periodDetails.length} of ${periodsToProcess.length} periods`)

      // Aggregate the details from successful requests
      details = aggregateTeamDetails(periodDetails)
    } else {
      // Just get current period details
      details = await loadTeamDetailsForPeriod(batchNumber)
    }

    // Exclude customers (class CUSTOMER) and inactive/departed consultants (cancelled, pending
    // resignation, deceased, terminated, resigned) so the grid, leaderboard, and filter dropdowns
    // show valid consultants only. Anchor the exclusion on the structure response (it always carries
    // `class`/`status` and covers more downline levels than the details response), then apply it to
    // both payloads.
    const isExcludedMember = (m: ITeamMemberResponse) =>
      m.class?.id === CUSTOMER_CLASS_ID ||
      (m.status?.statusId !== undefined &&
        (REPORT_EXCLUDED_STATUS_IDS as readonly string[]).includes(m.status.statusId))

    const excludedBeeNumbers = new Set(
      (structure?.item?.teamMembers ?? [])
        .filter(isExcludedMember)
        .map(m => m.beeNumber.beeNumber),
    )
    if (structure?.item?.teamMembers) {
      structure.item.teamMembers = structure.item.teamMembers.filter(
        m => !isExcludedMember(m),
      )
    }
    if (details?.item?.team) {
      details.item.team = details.item.team.filter(
        (t: { teamMember?: { beeNumber?: { beeNumber?: number } } }) => {
          const bn = t.teamMember?.beeNumber?.beeNumber
          return bn === undefined || !excludedBeeNumbers.has(bn)
        },
      )
    }

    // Log business details response
    logger.info('Business details response:', businessDetailsResponse)
    // Store team details response in appropriate ref
    if (selectedTimePeriod.value === 'thisYear') {
      yearDetailsResponse.value = details
    } else {
      monthDetailsResponse.value = details
    }
    logger.info('Team details response:', details)

    // Update grid and leaderboard with new data
    updateTeamGrid()
    updateLeaderboard()
      if(businessDetailsResponse.currentFields){
        consultantTitle.value = businessDetailsResponse.currentFields.find((f: any) => f.fieldName === 'PaidAtLevelText')?.value || t('consultantCorner.reportDashboard.defaultConsultantTitle')
      }
    // Store structure response
    structureResponse.value = structure as ITeamStructureResponse

    if (detailsResponse.value?.item?.team) {
      // Create a map of beeNumber to fieldList for quick lookup
      const teamDetailsMap = new Map<number, ITeamDetailsField[]>()
      const leaderboardData: { beeNumber: number; name: string; value: number }[] = []

      // Process team details for metrics
      detailsResponse.value.item.team.forEach((item: any) => {
        const member = item.teamMember
        const fieldList = item.fieldList || []
        if (fieldList.length > 0) {
          teamDetailsMap.set(member.beeNumber.beeNumber, fieldList)
        }
      })

      // Update the leaderboard
      updateLeaderboard()

      // Map team members for grid
      const mappedMembers = structureResponse.value.item.teamMembers.map((member: ITeamMemberResponse) => {
        const fieldList = teamDetailsMap.get(member.beeNumber.beeNumber) || []
        const getFieldValue = (fieldName: string) => {
          const field = fieldList.find((f: ITeamDetailsField) => f.fieldName === fieldName)
          return field?.value || 0
        }

        const mPersonalSales = getFieldValue('TotalPersCommVol')
        const mTeamRecruits = getFieldValue('newrecs')
        const paidAtTextField = fieldList.find((f: ITeamDetailsField) => f.fieldName === 'PaidAtLevelText')
        const mPaidAtLevelText = paidAtTextField ? String(paidAtTextField.value) : null
        const mCompLevelName = member.compensationLevel.levelName
          ? String(member.compensationLevel.levelName)
          : null
        return {
          beeNumber: member.beeNumber.beeNumber,
          name: `${member.firstName} ${member.lastName}`,
          title: member.compensationLevel.levelName,
          status: getStatusColor(
            member.status.statusId,
            mPersonalSales,
            mTeamRecruits,
            mPaidAtLevelText,
            mCompLevelName
          ),
          statusName: member.status.statusName || '',
          email: member.emailAddress || '',
          personalSales: mPersonalSales,
          personalTeamSales: getFieldValue('TotalTeamCommVolume'),
          teamRecruits: mTeamRecruits,
          teamEndCount: getFieldValue('PersCons'),
          activeConsultants: getFieldValue('ActiveCons')
        }
      })

      teamMembers.value = mappedMembers

      logger.info('Team members loaded:', {
        count: teamMembers.value.length,
        members: teamMembers.value
      })
    } else {
      teamMembers.value = []
    }
  } catch (error) {
    logger.error('Error loading team data:', error)
    await handleError(error, t)
  }
}

const loadLeaderboard = async () => {
  logger.info('Loading leaderboard for:', selectedLeaderboardFilter.value)
}

onMounted(async () => {
  try {
    reportBaseUrl.value = await GetReportUrl()
    // Call performance metrics APIs
    const [metricsResponse, performanceResponse] = await Promise.all([
      PwsPerformanceMetricsSummaryGet(),
      PwsPerformanceMetricsGet()
    ])
    logger.info('Performance Metrics Summary Response:', metricsResponse)

    // Store the performance metrics response and handle API structure
    if (performanceResponse?.items) {
      performanceMetrics.value = performanceResponse
    } else {
      logger.warn('Unexpected API response structure:', performanceResponse)
    }

    // First get period dates to get batchNumber
    const periodDatesResponse = await PwsAppPeriodDateGetList()
    if (!periodDatesResponse?.items?.length) {
      throw new Error(t('consultantCorner.reportDashboard.errors.noPeriodDatesAvailable'))
    }

    // Use first period's batchNumber
    const batchNumber = periodDatesResponse.items[0].batchNumber
    logger.info('Using batch number:', batchNumber)

    await Promise.all([
      loadMetrics(batchNumber),
      loadTeamData(batchNumber),
      loadLeaderboard()
    ])

    // Mark month data as loaded since we start on thisMonth view
    monthDataLoaded.value = true
  } catch (error) {
    logger.error('Error in component initialization:', error)
    await handleError(error, t)
  }
})
</script>


<style lang="scss">
.container:has(.cc-leaderboard-section){
  max-width:unset !important;
}
.cc-performance-sections  .cc-section-title {margin-top:unset;}
</style>
<style lang="scss" scoped>
.cc-report-dashboard-container {
  display: flex;
  flex-direction: row;
  gap: $spacing-5;
  padding: $card-padding;
  align-items: stretch;
}


.cc-main-content {
  flex: 1;
}

.cc-leaderboard-section {
  background: white;
  border-radius: $radius-md;
  box-shadow: 0 1px 3px rgba(black, 0.1);
  padding: 1rem;
  flex: 1;
}

.cc-header {
  background-color: white;
  color: #2c7a87;
  padding: $spacing-2-5 $spacing-4;
  text-align: left;
  border-bottom: 1px solid rgba(white, 0.1);
}

.cc-header-title {
  @include typography-h4;
  @include font-weight-medium;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color:black;
  margin-left:5px;
}

.cc-notification-bar {
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
  padding: $spacing-3 $spacing-5;
  text-align: center;
  margin-bottom: 20px;
}

.cc-notification-content {
  @include typography-small;
  @include font-weight-medium;
}

.cc-title-highlight {
  @include font-weight-semibold;
  color: #2c7a87;
  text-decoration: underline;
}

.cc-time-filter {
  display: flex;
  justify-content: center;

}

.cc-filter-buttons {
  display: flex;
  gap: $spacing-1;
}

.cc-filter-button {
  padding: $spacing-2 $spacing-4;
  border: none;
  @include typography-small;
  background: none;
  color: #2c7a87;
  cursor: pointer;
  border-radius: $radius-lg;
  transition: all 0.2s;
}

.cc-filter-button:hover {
  background-color: var(--vt-c-white-soft);
}

.cc-filter-button.active {
text-decoration: underline;
}

.cc-metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: $spacing-5;
  margin-bottom: $spacing-8;
}

.cc-metric-card {
  background: rgba(#2c7a87, 0.051);
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  padding: $card-padding;
  display: flex;
  align-items: center;
  gap: $spacing-4;
  box-shadow: 0 2px 4px rgba(black, 0.1);
  transition: transform 0.2s ease;
}

.cc-metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(black, 0.2);
}

.cc-metric-icon {
  @include typography-h3;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--vt-c-white-soft);
  border-radius: 50%;
}

.cc-metric-content {
  flex: 1;
}

.cc-metric-label {
  @include typography-caption;
  color: #666;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.cc-metric-value {
  @include typography-h3;
  @include font-weight-bold;
  margin-bottom: 5px;
  color: #2c7a87;
}

.cc-performance-sections {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: $spacing-8;
  margin-bottom: $spacing-10;
}

.cc-performance-section, .cc-leaderboard-section, .cc-team-section {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  padding: $card-padding;
}
.cc-leaderboard-section{
  background: rgba(#2c7a87, 0.051) !important;
}

.cc-section-title {
  @include typography-h4;
  @include font-weight-semibold;
  margin-bottom: 15px;
  color: #2c3e50;
}

.cc-indicator-grid {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

}

.cc-indicator-item {
  background: white;
  border-radius: $radius-md;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
  border: 1px solid #e9ecef;
  min-height: 3.5rem;
}

.cc-indicator-number {
  @include typography-h4;
  @include font-weight-semibold;
  margin-right: 1rem;
  min-width: 1.5rem;
  color: #111827;
}

.cc-indicator-label {
  @include typography-small;
  color: #374151;
  flex-grow: 1;
}

.cc-indicator-item:after {
  @include typography-h3;
  content: '›';
  color: #9CA3AF;
  margin-left: 0.5rem;
}

.cc-risk-item {
  background-color: #fff5f5;
  border: 1px solid #FEE2E2;
}

.cc-risk-item .cc-indicator-number {
  color: #EF4444;
}

.cc-success-item {
  background-color: #F0FDF4;
  border: 1px solid #DCFCE7;
}

.cc-success-item .cc-indicator-number {
  color: #22C55E;
}
.cc-leaderboard-section,
.cc-team-section {
  margin-bottom: 30px;
}

.cc-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
  flex-wrap: wrap;
  gap: $spacing-4;
}
.cc-metric-card.title-bar{margin-bottom:$spacing-5;}
.cc-leaderboard-filters{margin-bottom:10px;}
.cc-leaderboard-filters,
.cc-team-filters {
  display: flex;
  gap: $spacing-2-5;
  align-items: center;
}

.cc-recognition-button {
  background-color: #2c7a87;
  color: white;
  @include typography-caption;
  border: none;
  padding: $spacing-2 $spacing-4;
  border-radius: $radius-sm;
  margin-top:15px;
  width:100%;
}

.cc-leaderboard-grid {
  display: grid;
  gap: $spacing-2-5;
}

.cc-leader-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: $spacing-3 $spacing-4;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  gap: $spacing-4;
  justify-content: space-evenly;
}

.cc-leader-rank {
  @include font-weight-semibold;
  color: #3498db;
}

.cc-leader-name {
  @include font-weight-medium;
}

.cc-leader-value {
  @include font-weight-semibold;
  color: #27ae60;
}

.cc-team-grid-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.cc-team-grid {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  overflow: hidden;
  min-width: 600px; /* Sum of column minimums: 60 + 100 + 80 + 80 + 80 + 60 + 60 + padding */
}

.cc-grid-header {
  display: grid;
  grid-template-columns: 60px minmax(100px, 1fr) minmax(80px, 1fr) minmax(80px, 1fr) minmax(80px, 1fr) minmax(60px, 1fr) minmax(60px, 1fr) minmax(60px, 1fr);
  background-color: var(--vt-c-white-soft);
  border-bottom: 2px solid #e9ecef;
}

.cc-grid-header-cell {
  @include typography-caption;
  @include font-weight-semibold;
  padding: $spacing-3 $spacing-2;
  text-transform: uppercase;
  color: #333;
  border-right: 1px solid #e9ecef;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: $spacing-1;
}

.cc-grid-header-cell:last-child {
  border-right: none;
}

.cc-grid-header-cell:hover {
  background-color: #e9ecef;
}

.cc-grid-body {
  max-height: 400px;
  overflow-y: auto;
}

.cc-grid-row {
  display: grid;
  grid-template-columns: 60px minmax(100px, 1fr) minmax(80px, 1fr) minmax(80px, 1fr) minmax(80px, 1fr) minmax(60px, 1fr) minmax(60px, 1fr) minmax(60px, 1fr);
  border-bottom: 1px solid var(--vt-c-white-soft);
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.cc-grid-row:hover {
  background-color: var(--vt-c-white-soft);
}

.cc-grid-cell {
  @include typography-small;
  padding: $spacing-3 $spacing-2;
  border-right: 1px solid var(--vt-c-white-soft);
  display: flex;
  align-items: center;
}

.cc-grid-cell:last-child {
  border-right: none;
}

.cc-name-cell {
  @include font-weight-medium;
  color: #3498db;
}

.cc-status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin: 0 auto;
}

.cc-status-green {
  background-color: #27ae60;
}

.cc-status-yellow {
  background-color: #f39c12;
}
.cc-start{background-color: rgba(#2c7a87, 0.051);}
.cc-start .cc-indicator-number{
  color:#2c7a87;
}
.cc-status-red {
  background-color: #e74c3c;
}

.cc-status-gray {
  background-color: #95a5a6;
}

.cc-sort-asc,
.cc-sort-desc {
  @include typography-caption;
  color: #3498db;
}

.cc-pagination-container {
  display: flex;
  justify-content: center;
  padding: $card-padding;
  background: white;
  border-top: 1px solid #e9ecef;
}

@media (max-width: 768px) {
  .cc-pagination-container :deep(.k-pager) {
    width: 80vw;
  }

  .cc-pagination-container :deep(.k-pager-numbers) {
    display: none;
  }
}
@media (min-width: 768px) {
.cc-leaderboard-container{padding-top:49px;}
}

.cc-clickable-name {
  color: #2c7a87;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.2s ease;
}

.cc-clickable-name:hover {
  @include font-weight-semibold;
  color: #1e4b54;
}

/* Inline detail card */
.cc-inline-detail-card {
  margin-top: 20px;
  margin-bottom: 20px;
  padding: 0 $spacing-5;
}

.cc-leaderboard-container {
  width: 300px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

@media (max-width: 768px) {
  .cc-report-dashboard-container {
    flex-direction: column;
    padding: $spacing-2;
    overflow-x: hidden;
  }

  .cc-leaderboard-container {
    width: 100%;
  }

  .cc-leaderboard-section {
    width: 100%;
  }

  .cc-metrics-grid {
    grid-template-columns: 1fr;
    padding: 0;
    gap: $spacing-3;
  }

  .cc-metric-card {
    gap: $spacing-3;
    padding: $spacing-3;
  }

  .cc-metric-card.title-bar {
    margin: $spacing-3 0;
  }

  .cc-performance-sections {
    grid-template-columns: 1fr;
    padding: 0;
    gap: $spacing-4;
  }

  .cc-section-header {
    flex-direction: column;
    align-items: stretch;
  }

  .cc-leaderboard-filters,
  .cc-team-filters {
    flex-wrap: wrap;
  }

  .cc-team-section,
  .cc-leaderboard-section {
    padding: 0 $spacing-3;
  }

  .cc-team-grid-wrapper {
    margin: 0 (-$spacing-3);
    padding: 0 $spacing-3;
  }

  .cc-grid-header-cell,
  .cc-grid-cell {
    @include typography-caption;
    padding: $spacing-2-5 $spacing-2;
  }

  .cc-team-desktop-view {
    display: none !important;
  }

  .cc-team-mobile-card-view {
    display: block !important;
  }

  .cc-inline-detail-card {
    padding: 0;
  }
}

/* Mobile Card View */
.cc-team-desktop-view {
  display: block;
}

.cc-team-mobile-card-view {
  display: none;
}

.cc-member-card {
  background: var(--vt-c-white);
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  overflow: hidden;
  margin-bottom: $spacing-4;
  transition: box-shadow 0.2s ease;
}

.cc-member-card:hover {
  box-shadow: 0 4px 12px rgba(black, 0.1);
}

.cc-member-card-header {
  padding: $spacing-4;
  background-color: var(--vt-c-white-soft);
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: $spacing-2-5;
  cursor: pointer;
}

.cc-member-card-name-row {
  display: flex;
  align-items: center;
  gap: $gap-sm;
}

.cc-member-card-name {
  @include typography-small;
  @include font-weight-semibold;
  color: #2c7a87;
  text-decoration: underline;
}

.cc-member-card-title {
  @include typography-small;
  color: #666;
}

.cc-member-card-body {
  padding: $spacing-4;
}

.cc-member-card-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: $spacing-2 0;
  border-bottom: 1px solid var(--vt-c-white-soft);
  gap: $spacing-2-5;
}

.cc-member-card-row:last-child {
  border-bottom: none;
}

.cc-member-card-label {
  @include typography-small;
  @include font-weight-medium;
  color: #666;
  flex-shrink: 0;
}

.cc-member-card-value {
  @include typography-small;
  color: #333;
  text-align: right;
}
</style>
